"use client";
import Link from "next/link";
import React, { useEffect, useState, ChangeEvent, FormEvent, useRef } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import Image from 'next/image';
import Head from 'next/head';
import { ReusableButtonLong, ReusableButtonLong1 } from "@/components/Button";
import Alert from '@/components/Alert';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

interface SignupPageProps {
  timestamp: number;
}

export const getStaticProps = async () => {
  const timestamp = Date.now(); // Generate timestamp once

  return {
    props: {
      timestamp,
    },
  };
};

interface User {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;
  address: string;
  password: string;
  confirmPassword: string;
  pin: string;
  confirmPin: string;
}

const INITIAL_USER_STATE: User = {
  firstName: "",
  lastName: "",
  username: "",
  email: "",
  phone: "",
  address: "",
  password: "",
  confirmPassword: "",
  pin: "",
  confirmPin: ""
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

export default function SignupPage({ timestamp }: SignupPageProps) {
  const router = useRouter();
  const [user, setUser] = useState<User>(INITIAL_USER_STATE);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning', message: string }>({ type: 'info', message: '' });
  const [showPassword, setShowPassword] = useState(false);

  const validateInput = (): string | null => {
    if (!EMAIL_REGEX.test(user.email)) {
      return "Invalid email format.";
    }
    if (!PASSWORD_REGEX.test(user.password)) {
      return "Password must be at least 8 characters long and include uppercase, lowercase, digit, and special character.";
    }
    if (user.password !== user.confirmPassword) {
      return "Passwords do not match.";
    }
    if (user.pin !== user.confirmPin) {
      return "Pins do not match.";
    }
    return null;
  };

  const onSignup = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationError = validateInput();
    if (validationError) {
      setAlert({ type: 'error', message: validationError });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post("/api/signup", user);
      
      if (response.data.message) {
        setAlert({ type: 'success', message: response.data.message });
      } else if (response.data.error === 'User already exists. Check your inbox or spam folder for verification email') {
        setAlert({ type: 'error', message: response.data.error });
        setTimeout(() => router.push('/reverify-email'), 2000);
      } else {
        setAlert({ type: 'error', message: response.data.error });
      }
    } catch (error) {
      console.error(error);
      setAlert({ type: 'error', message: 'Internal Server Error. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    if (formRef.current) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  useEffect(() => {
    setButtonDisabled(
      !(user.username && user.email && user.password && user.confirmPassword && user.pin && user.confirmPin && user.firstName && user.lastName && user.phone)
    );
  }, [user]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setUser((prevUser) => ({ ...prevUser, [id]: value }));

    if (id === "password") {
      updatePasswordStrength(value);
    }
  };

  const updatePasswordStrength = (password: string) => {
    if (PASSWORD_REGEX.test(password)) {
      setPasswordStrength("Strong");
    } else if (password.length >= 6) {
      setPasswordStrength("Medium");
    } else {
      setPasswordStrength("Weak");
    }
  };

  return (
    <main>
      <Head>
        <title>Signup</title>
        <meta name="description" content="Buy cheap data bundles, airtime online for MTN, Glo, Airtel, and 9mobile, cable tv subscriptions, electricity bills payment, education pins. Swiftly process automated services from all service providers." />
        <meta name="keywords" content="cheap-data-bundles, buy-data, airtime, top-up, MTN, Glo, Airtel, 9mobile, etisalat, VTU, cable-tv, dstv, gotv, startimes, showmax, electricity-payments, prepaid-meter, postpaid-meter, ikedc, ekedc, aedc, kedco, phed, jed, kaedco, eedc, ibedc, bedc, education-payments, exam-pins, neco, waec, jamb, utme, nabteb" />
      </Head>

      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <Link href="/">
        <Image
            src={`/logo.webp?v=${timestamp}`}
            alt="iabcodes"
            width={200}
            height={100}
            className="mx-auto h-10 w-auto"
          /></Link>
          <h1 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-black">
            {loading ? "Processing" : "Signup for an account"}
          </h1>
          <hr />
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form className="space-y-6" onSubmit={onSignup} ref={formRef}>
            {["firstName", "lastName", "username", "email", "phone"].map((field) => (
              <div key={field}>
                <label htmlFor={field} className="block text-sm font-medium text-black">
                  {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  id={field}
                  type="text"
                  value={user[field as keyof User]}
                  onChange={handleInputChange}
                  placeholder={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                  autoComplete={field}
                  className="primary"
                />
              </div>
            ))}

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-black">
                Address
              </label>
              <input
                id="address"
                type="text"
                value={user.address}
                onChange={handleInputChange}
                placeholder="Address"
                autoComplete="address"
                className="primary"
                />
            </div>

            {["password", "confirmPassword"].map((field) => (
              <div key={field}>
                <label htmlFor={field} className="block text-sm font-medium text-black">
                  {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                <input
                  id={field}
                  type={showPassword ? "text" : "password"}
                  value={user[field as keyof User]}
                  onChange={handleInputChange}
                  placeholder={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                  autoComplete={field}
                  className="primary"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button></div>
                {field === "password" && (
                  <div className={`text-sm ${passwordStrength === "Strong" ? "text-green-700" : passwordStrength === "Medium" ? "text-yellow-700" : "text-red-700"}`}>
                    Password Strength: {passwordStrength}
                  </div>
                )}
              </div>
            ))}

            {["pin", "confirmPin"].map((field) => (
              <div key={field}>
                <label htmlFor={field} className="block text-sm font-medium text-black">
                  {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                <input
                  id={field}
                  type={showPassword ? "text" : "password"}
                  value={user[field as keyof User]}
                  onChange={handleInputChange}
                  placeholder={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                  autoComplete={field}
                  inputMode="numeric"
                  maxLength={4}
                  pattern="\d{4}"
                  className="primary"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button></div>
              </div>
            ))}

            <div>{alert.message && (<Alert type={alert.type} message={alert.message} onClose={() => setAlert({ type: 'info', message: '' })}/>)}</div>

            {buttonDisabled ? <ReusableButtonLong1 onClick={handleButtonClick} loading={loading} disabled={buttonDisabled} buttonText="Signup" />
            : <ReusableButtonLong onClick={handleButtonClick} loading={loading} disabled={buttonDisabled} buttonText="Signup" />}
          </form>

          <p className="mt-10 text-center text-sm text-black">
            Already a member?{' '}
            <Link href="/login" className="font-semibold leading-6 text-primary hover:text-secondary">
              Login
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
